import React, { useEffect, useRef, useState } from 'react';
import { ScrollMenu } from 'react-horizontal-scrolling-menu';
import './nav-filters-all.scss';
import VideoHlroomAnalytics from '@sky-it-video/hlroom-analytics';
import { useIntersectionObserver } from 'usehooks-ts';

import { IClassNames, makeClassNames } from '../../../utils/styles';
import useDrag from '../../../hooks/useDrag';
import SquareFilterImage from '../../atoms/square-filter-image/square-filter-image';
import TypograpyHeadingComponent from '../../foundation/typography/typography-heading/typography-heading';
import TypograpyBodyComponent from '../../foundation/typography/typography-body/typography-body';
import { ISubSection } from '../../../typings/hlroom';

export interface INavFiltersAll {
  onSubSectionChange: (subSection: ISubSection) => void;
  isDesktop: boolean;
  subSections: ISubSection[];
  title: string;
  description: string;
  onVisibilityChange: (value: boolean) => void;
  appType?: string;
}
const NavFiltersAll: React.FC<INavFiltersAll> = ({
  onSubSectionChange,
  isDesktop = false,
  subSections,
  title,
  description,
  onVisibilityChange,
  appType = 'sport',
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const entry = useIntersectionObserver(ref, { threshold: 0.2 });
  const [selectedItem, setSelectedItem] = useState(subSections[0]);
  const { dragStart, dragStop, dragMove, dragging } = useDrag();

  useEffect(() => {
    if (!entry?.isIntersecting) {
      return onVisibilityChange(false);
    }
    onVisibilityChange(true);
  }, [entry?.isIntersecting]);

  const isDefaultFilter = (filter: number) => filter === 0;

  const handleDrag =
    ({ scrollContainer }: any) =>
    (ev: React.MouseEvent) =>
      dragMove(ev, (posDiff) => {
        if (scrollContainer.current) {
          scrollContainer.current.scrollLeft += posDiff;
        }
      });

  const classNames = (isDesktop: boolean): IClassNames =>
    makeClassNames({
      navFiltersAll: [
        'nav-filters-all',
        isDefaultFilter(0) && !isDesktop && 'nav-filters-all--hide',
        isDesktop && 'nav-filters-all__desktop',
      ],
      navFiltersAllTitle: 'nav-filters-all__title',
      navFiltersAllDescription: 'nav-filters-all__description ty-body-small',
      navFilterAllWrapper: 'nav-filters-all__wrapper',
      navFilterAllContainer: 'nav-filters-all__container',
    });

  const cn = classNames(isDesktop);

  const clickOnChip = (subSection: ISubSection) => {
    if (dragging) {
      return false;
    }

    VideoHlroomAnalytics.setDigitalData(
      {
        zoneid: { subSection: !subSection?.tag ? 'tutti' : subSection?.tag?.replace(' ', '-') },
      },
      appType
    );
    onSubSectionChange(subSection);
    setSelectedItem(subSection);
    return false;
  };

  return (
    <div className={cn.navFiltersAll} onMouseLeave={dragStop} ref={!isDesktop ? ref : null}>
      {appType !== 'tg24' && (
        <React.Fragment>
          <div className={cn.navFiltersAllTitle}>
            <TypograpyHeadingComponent customFont="skyHeadline--l" headingTag="h2" text={title} />
          </div>
          <TypograpyBodyComponent typographyClass={cn.navFiltersAllDescription} text={description} />
        </React.Fragment>
      )}
      {!isDesktop && (
        <ScrollMenu
          scrollContainerClassName={cn.navFilterAllContainer}
          onMouseDown={() => dragStart}
          onMouseUp={() => dragStop}
          onMouseMove={handleDrag}
          wrapperClassName={cn.navFilterAllWrapper}
        >
          {subSections.map((s, i) => (
            <SquareFilterImage
              key={i}
              isDesktop={isDesktop}
              onClick={() => clickOnChip(s)}
              subSection={s}
              selectedItem={selectedItem}
              appType={appType}
            />
          ))}
        </ScrollMenu>
      )}
    </div>
  );
};

export default NavFiltersAll;
